import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { api, CAP_LABELS } from "@/lib/api";
import { Wordmark } from "@/components/Wordmark";

const FILTERS = ["all", "push", "pull", "squat", "bend_lift", "rotation", "endurance"];
const CARD_IMAGES = ["/images/uploads/tyre.jpg", "/images/uploads/ropes.jpg", "/images/uploads/partners.jpg"];

export default function PublicWorkouts() {
  const [workouts, setWorkouts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const q = filter === "all" ? "" : `?capability=${filter}`;
    api.get(`/public/workouts${q}`).then((r) => setWorkouts(r.data)).catch(() => {});
  }, [filter]);

  const openDetail = async (id) => {
    if (open === id) return setOpen(null);
    const { data } = await api.get(`/public/workouts/${id}`);
    setDetail(data);
    setOpen(id);
  };

  return (
    <div className="min-h-screen bg-ink text-white">
      <div className="grain-overlay" aria-hidden="true" />
      <header className="flex items-center justify-between px-5 sm:px-10 h-16 border-b border-zinc-800/70">
        <Link to="/" data-testid="workouts-home-link"><Wordmark className="text-2xl" /></Link>
        <Link to="/assessment" data-testid="workouts-assessment-link"
          className="text-[10px] uppercase tracking-[0.22em] font-bold text-volt hover:text-white transition-colors">
          Take the assessment
        </Link>
      </header>

      <main className="px-5 sm:px-10 lg:px-16 py-14 sm:py-20 max-w-6xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-volt transition-colors mb-8">
          <ArrowLeft size={14} /> Home
        </Link>
        <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-volt mb-4">Capable 360 Training Sessions</p>
        <h1 className="font-display uppercase text-5xl sm:text-7xl leading-[0.9]">Train with <span className="text-stroke">purpose.</span></h1>
        <p className="mt-5 text-zinc-400 max-w-xl text-sm sm:text-base">
          Every session develops a capability you can use in real life.
        </p>

        <div className="flex flex-wrap gap-2 mt-10">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`workout-filter-${f}`} onClick={() => setFilter(f)}
              className={`border px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] font-bold transition-colors ${filter === f ? "bg-volt text-black border-volt" : "border-zinc-700 text-zinc-400 hover:border-volt"}`}>
              {f === "all" ? "All" : CAP_LABELS[f]}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-8">
          {workouts.map((w, i) => (
            <article key={w.id} data-testid={`workout-card-${w.id}`}
              className="border border-zinc-800 bg-zinc-950 overflow-hidden hover:border-volt/50 transition-colors">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={CARD_IMAGES[i % CARD_IMAGES.length]} alt={`${w.name} — functional training`} loading="lazy"
                  className="h-full w-full object-cover object-center" />
                <span className="absolute top-3 left-3 text-[9px] uppercase tracking-[0.18em] font-extrabold text-black bg-volt px-2.5 py-1">
                  {w.difficulty || "All levels"}
                </span>
              </div>
              <div className="p-6">
                <h2 className="font-display uppercase text-3xl leading-none">{w.name}</h2>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[10px] uppercase tracking-[0.14em] font-bold text-zinc-500">
                  <span className="flex items-center gap-1.5"><Clock size={12} className="text-volt" /> {w.duration_minutes} min</span>
                  <span className="text-volt">{CAP_LABELS[w.primary_capability]}</span>
                  <span>RPE {w.target_rpe}</span>
                </div>
                <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{w.objective}</p>
                <button data-testid={`workout-view-${w.id}`} onClick={() => openDetail(w.id)}
                  className="group mt-5 inline-flex items-center gap-2 text-volt text-xs uppercase tracking-[0.18em] font-bold hover:text-white transition-colors">
                  {open === w.id ? "Close session" : "View session"}
                  <ArrowRight size={14} className={`transition-transform ${open === w.id ? "rotate-90" : "group-hover:translate-x-1"}`} />
                </button>

                {open === w.id && detail && (
                  <div className="mt-5 pt-5 border-t border-zinc-800 space-y-4">
                    {detail.blocks.map((b) => (
                      <div key={b.key}>
                        <p className="text-[9px] uppercase tracking-[0.22em] font-extrabold text-volt mb-1.5">{b.name} · {b.minutes}m</p>
                        <ul className="space-y-1.5">
                          {b.items.map((it) => (
                            <li key={it.movement_id} className="flex justify-between gap-3 text-sm">
                              <span className="text-zinc-200">{it.name}</span>
                              <span className="text-zinc-500 whitespace-nowrap">{it.prescription}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    <Link to="/assessment" data-testid={`workout-cta-${w.id}`}
                      className="inline-block bg-volt text-black uppercase tracking-[0.16em] text-xs font-extrabold px-6 py-3.5 hover:bg-white transition-colors">
                      Train this with us — start with the assessment
                    </Link>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
        {!workouts.length && (
          <p className="text-zinc-500 mt-10 text-sm">No public sessions in this category yet — check back soon.</p>
        )}
      </main>
    </div>
  );
}
